/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-ACTIONS — Status-/Zahlungs-Handler + Probesaft-Geste
   =============================================================================
   S201 REF-7e: aus orders.js extrahiert (Null Verhaltensänderung). Bündelt die
   mutierenden Karten-Handler des Bestell-Boards + den zugehörigen Modul-State:
     · _probesaftAck (Set der order-ids) — überlebt Realtime-Re-Renders; von
       orders-cards.js gelesen, hier geschrieben (orderProbesaft). Single-Source.
     · orderSetStatus / orderCancel / orderMarkPaid (Zahlung + TSE + Auto-Archiv +
       Auto-Print + Tisch-Freigabe-Fallback) · orderMarkSeen / rehydrateOrderSeenFlags
       (Seen-Marker via localStorage) · shouldPromptReceiptOnPaid.
   Inline-Handler-Bindings (func(), kein window.func()) setzt dieses Modul selbst.
   Importiert KEINEN Orchestrator (acyclisch).
============================================================================ */

import { showToast, fmtEUR } from '../../utils/helpers.js';
import { rerenderActiveTab } from '../../utils/dom.js';
import { hasPreparedGoods, isStornoLocked } from '../../core/order-statuses.js';
import { openSchwundCancelModal, handlePayGuardError } from './orders-split.js';
import { _ensureBonArchived } from './orders-receipt-actions.js';
import { openReceiptActionModal } from './orders-receipt-actions.js';
import { STATUS_LABEL } from './orders-data.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

/* ===========================================================================
   S171 — Probesaft-Geste (Haus-Gruß): vor „Bar/Karte" muss der Operator den
   frisch gepressten Probesaft ausgegeben + bestätigt haben. Pro Bestellung ein
   bewusster Klick → schaltet die Zahlbuttons frei. Modul-State (Set der order-
   ids) überlebt die Realtime-Re-Renders; Session-lokal (Reset bei Reload ok).
   Von orders-cards.js importiert (gleiche Set-Instanz via ESM).
=========================================================================== */
export const _probesaftAck = new Set();

/** S171 — Probesaft-Geste bestätigt → schaltet Bar/Karte für DIESE Bestellung frei. */
export function orderProbesaft(id) {
  if (!id) return;
  _probesaftAck.add(String(id));
  rerenderActiveTab();
}

// -----------------------------------------------------------------------------
// Status-Wechsel-Handler
// -----------------------------------------------------------------------------

/**
 * Status der Bestellung wechseln. Emittiert `order:status_changed` mit
 * { order, from, to } für die Mail-Pipeline (bootstrap.js). Optimistic-
 * Update fängt langsame Realtime-Pings ab.
 */
export async function orderSetStatus(id, nextStatus) {
  const state = getState();
  const prev = (state.orders || []).find(o => o.id === id);
  if (!prev) return showToast('Bestellung nicht gefunden', 'error');
  const fromStatus = prev.status;
  if (fromStatus === nextStatus) return;

  // S143 / Ticket 2 — No-Show einer bereits ZUBEREITETEN Order: nicht still in
  // no_show kippen (Warenverlust ginge verloren), sondern in den geführten
  // Schwund-Pfad routen (order_corrections ohne TSE + status=no_show).
  if (nextStatus === 'no_show' && prev.paymentStatus !== 'paid' && hasPreparedGoods(prev)) {
    return openSchwundCancelModal(id, 'no_show', prev);
  }

  // S99 — Defense-in-Depth: Lieferungen werden NIE über den Master-Status auf
  // picked_up gesetzt — das ist allein Sache des Fahrer-Cockpits.
  if (nextStatus === 'picked_up' && prev.serviceType === 'delivery') {
    return showToast('Lieferungen werden im Liefer-Cockpit auf „ausgeliefert" gesetzt — nicht hier.', 'warn');
  }

  try {
    const updated = await window.App.storage.orders.updateStatus(id, nextStatus);
    if (!updated) throw new Error('Update lieferte kein Ergebnis');
    if (window.App.events) {
      await window.App.events.emit('order:status_changed', {
        order: updated, from: fromStatus, to: nextStatus
      });
    }
    showToast(`Bestellung → ${STATUS_LABEL[nextStatus] || nextStatus}`, 'success');

    // Phase 12.1 — Küchen-Bon-Auto-Print
    try {
      const routing = (typeof window.printerGetRouting === 'function') ? window.printerGetRouting() : null;
      const items = await window.App.storage.orders.listItems(id).catch(() => []);
      const enriched = { ...updated, items };
      if (routing && nextStatus === 'confirmed' && routing.kitchenOnConfirm
          && typeof window.printerPrintKitchen === 'function') {
        window.printerPrintKitchen(enriched, { silent: true });
      }
      if (routing && nextStatus === 'preparing' && routing.kitchenOnPreparing
          && typeof window.printerPrintKitchen === 'function') {
        window.printerPrintKitchen(enriched, { silent: true });
      }
    } catch (printErr) {
      console.warn('[orderSetStatus] Küchen-Bon-Print fehlgeschlagen (nicht-fatal):', printErr);
    }
  } catch (e) {
    console.error('[orderSetStatus]', e);
    showToast('Statuswechsel fehlgeschlagen: ' + (e.message || e), 'error');
  }
  rerenderActiveTab();
}

// S192 — bulkConfirmOrders/bulkCancelOrders entfernt (Jonas-Direktive „immer
// einzeln"). Offene Bestellungen werden im Dashboard-„Anfragen"-Reiter je einzeln
// angenommen/abgelehnt (orderSetStatus …,'confirmed' / orderCancel).

/**
 * Storno-Pfad. update_order_status mit `cancelled` + dediziertes
 * `order:cancelled`-Event (damit die Cancel-Mail rausgeht, ohne dass die
 * status_changed-Pipeline doppelt feuert).
 */
export async function orderCancel(id) {
  const state = getState();
  const prev = (state.orders || []).find(o => o.id === id);
  if (!prev) return showToast('Bestellung nicht gefunden', 'error');
  // GoBD-Sperre: bezahlte Bestellungen dürfen nur über Korrekturbeleg storniert werden.
  if (prev.paymentStatus === 'paid') {
    showToast('Bezahlte Bestellung kann nur über Korrekturbeleg storniert werden (Phase 10a.2).', 'error');
    return;
  }
  // S143 / Ticket 2 — Storno einer bereits ZUBEREITETEN Order: zubereitete Ware
  // muss als Warenverlust gebucht werden (GoBD). Geführtes Schwund-Grund-Modal.
  if (hasPreparedGoods(prev)) {
    return openSchwundCancelModal(id, 'cancelled', prev);
  }
  // S75 Storno-Härtung: Bei aktiver Order (Küche/Bar/Lieferung) Warn-Confirm.
  const num = prev.orderNumber || prev.order_number || '';
  const confirmMsg = isStornoLocked(prev)
    ? `⚠️ Bestellung ${num} ist bereits in Zubereitung / Auslieferung.\nTrotzdem stornieren? Küche/Bar/Fahrer ggf. informieren.`
    : `Bestellung ${num} wirklich stornieren?`;
  if (!(await window.styledConfirm({ title: 'Bestellung stornieren?', body: confirmMsg, danger: true, confirmLabel: 'Stornieren' })).confirmed) return;

  try {
    const updated = await window.App.storage.orders.updateStatus(id, 'cancelled');
    if (!updated) throw new Error('Storno lieferte kein Ergebnis');
    if (window.App.events) {
      await window.App.events.emit('order:cancelled', {
        order: updated, by: 'restaurant'
      });
    }
    showToast('Bestellung storniert', 'success');
  } catch (e) {
    console.error('[orderCancel]', e);
    showToast('Storno fehlgeschlagen: ' + (e.message || e), 'error');
  }
  rerenderActiveTab();
}

/** Markiert die Bestellung als bezahlt (Bar / Karte). RPC vergibt atomisch eine
 *  Rechnungsnummer. Danach TSE-Signatur (await), Auto-Print, GARANTIERTE
 *  Bon-Archivierung (S57 §147 AO), Beleg-Aktion-Modal / Tisch-Freigabe-Fallback. */
export async function orderMarkPaid(id, method, discountId) {
  if (!['cash', 'card'].includes(method)) return;
  let updated;
  try {
    updated = await window.App.storage.orders.markPaid(id, method, discountId || null);
    if (!updated) throw new Error('Bezahl-Markierung lieferte kein Ergebnis');
    if (window.App.events) {
      await window.App.events.emit('order:paid', {
        order: updated, method
      });
    }
    const inv = updated.invoiceNumber || updated.invoice_number;
    const _disc = Number(updated.discountCents || 0);
    const _discNote = _disc > 0 ? ` · Rabatt ${updated.discountName ? updated.discountName + ' ' : ''}−${fmtEUR(_disc / 100)}` : '';
    showToast(`Als bezahlt markiert${inv ? ' · Rg. ' + inv : ''}${_discNote}`, 'success');
  } catch (e) {
    console.error('[orderMarkPaid]', e);
    // S247 — der Beleg-Guard aus phase_181 ist kein Bedienfehler, sondern ein
    // Zustand: an dieser Rechnung ist schon etwas abgerechnet, oder ein Gast
    // zahlt gerade online. Dann bekommt der Operator dieselbe Antwort wie beim
    // Vorab-Check (restOnly bzw. „Tisch zahlt gerade online") statt eines rohen
    // Fehlertexts, aus dem er nichts ableiten kann.
    let handled = false;
    try { handled = await handlePayGuardError(id, method, e); }
    catch (guardErr) { console.warn('[orderMarkPaid] Guard-Auswertung:', guardErr); }
    if (!handled) showToast('Bezahl-Markierung fehlgeschlagen: ' + (e.message || e), 'error');
    rerenderActiveTab();
    return;
  }

  // TSE-Signatur — await, damit der Bon im Modal die Signatur zeigt.
  if (typeof window.tseSignAndRecord === 'function') {
    try {
      const res = await window.tseSignAndRecord(updated);
      if (res && res.sig) {
        showToast(`TSE-Signatur (${res.sig.mode}) · Counter ${res.sig.signatureCounter}`, 'success');
      }
    } catch (err) {
      console.warn('[orderMarkPaid] TSE-Signatur fehlgeschlagen:', err);
      showToast('TSE-Signatur fehlgeschlagen: ' + (err.message || err), 'error');
    }
  }

  rerenderActiveTab();

  // Frisch aus der DB holen — Realtime kann nachhinken (Memory feedback_gg_export_fresh_db).
  let fresh = await window.App.storage.orders.getFresh(id).catch(() => null);
  if (!fresh) fresh = (getState().orders || []).find(o => o.id === id) || updated;

  // Items aus DB nachladen + an fresh mergen, damit der Bon-Renderer die Positions-
  // Zeilen zeigt (orders-Cache hat nur Header).
  try {
    const items = await window.App.storage.orders.listItems(id);
    if (items && items.length) {
      fresh = { ...fresh, preorderSnapshot: {
        version: 2,
        items: items.map(it => ({
          name:             it.nameSnapshot || it.name_snapshot,
          qty:              it.qty,
          unit_price_cents: it.unitPriceCents || it.unit_price_cents,
          line_total_cents: it.lineTotalCents || it.line_total_cents,
          tax_rate:         it.taxRate || it.tax_rate,
          note:             it.notes || (it.customizations && it.customizations.note)
        }))
      } };
    }
  } catch (_) { /* nicht-fatal: Bon zeigt ggf. „Keine Positionen" */ }

  // Phase 12.1 — Bon-Auto-Print (silent), sobald Bon-Drucker konfiguriert sind.
  try {
    const routing = (typeof window.printerGetRouting === 'function') ? window.printerGetRouting() : null;
    if (routing && routing.autoOnPaid && typeof window.printerPrintReceipt === 'function') {
      const items = await window.App.storage.orders.listItems(id).catch(() => []);
      const enriched = { ...fresh, items };
      window.printerPrintReceipt(enriched, { silent: true });
    }
  } catch (printErr) {
    console.warn('[orderMarkPaid] Bon-Auto-Print fehlgeschlagen (nicht-fatal):', printErr);
  }

  // S57 Hard-Way (GoBD §147 AO): Bon-Archivierung GARANTIERT (idempotent).
  const archiveResult = await _ensureBonArchived(fresh, { source: 'auto' });
  if (!archiveResult.archived) {
    showToast(
      '⚠️ KRITISCH: Bon-Archivierung fehlgeschlagen — ' + (archiveResult.error || 'Unbekannter Fehler') +
      '. Bitte Bon manuell über das Bon-Popup archivieren oder Support kontaktieren.',
      'error'
    );
  }

  // S134 P3 — Tisch-Freigabe ist bewusste Operator-Entscheidung im Bon-Modal.
  // Fallback: ist receiptPromptOnPaid=false (kein Modal), bleibt für Walk-Ins die
  // alte Auto-Freigabe erhalten — sonst bliebe der Tisch belegt.
  if (shouldPromptReceiptOnPaid()) {
    openReceiptActionModal(fresh, { kind: 'paid' });
  } else if (fresh && fresh.occupancyId && window.App && window.App.supabase) {
    try {
      const { error: endErr } = await window.App.supabase.rpc('auto_end_occupancy_for_order', { p_order_id: fresh.id });
      if (endErr) console.warn('[orderMarkPaid] auto_end_occupancy_for_order:', endErr.message);
      else if (typeof window.showToast === 'function') showToast('Tisch wieder frei — Walk-In beendet.', 'success');
    } catch (occErr) {
      console.warn('[orderMarkPaid] auto-end occupancy threw:', occErr);
    }
  }
}

/** Liest settings.receiptPromptOnPaid mit defensivem Default true. */
export function shouldPromptReceiptOnPaid() {
  const s = (getState() && getState().settings) || {};
  if (Object.prototype.hasOwnProperty.call(s, 'receiptPromptOnPaid')) {
    return !!s.receiptPromptOnPaid;
  }
  return true;
}

/** Karten-Klick = „gesehen", entfernt die Pulse-Animation (non-destruktiv). */
export function orderMarkSeen(id) {
  const state = getState();
  const o = (state.orders || []).find(x => x.id === id);
  if (!o) return;
  if (o._seen) return;
  o._seen = true;
  // Lokal persistieren, damit das Re-Render nach Realtime-Patch konsistent ist.
  try {
    const seen = JSON.parse(localStorage.getItem('gg.orders.seen') || '[]');
    if (!seen.includes(id)) {
      seen.push(id);
      while (seen.length > 200) seen.shift();   // Cap auf 200 jüngste
      localStorage.setItem('gg.orders.seen', JSON.stringify(seen));
    }
  } catch (_) {}
  // Pulse-Klasse direkt entfernen — kein Full-Re-Render nötig.
  const card = document.querySelector(`.order-card[data-id="${id}"]`);
  if (card) card.classList.remove('order-fresh');
}

/** Beim Re-Render aus localStorage seen-Marker auf die Cache-Rows zurückspielen. */
export function rehydrateOrderSeenFlags() {
  try {
    const seen = JSON.parse(localStorage.getItem('gg.orders.seen') || '[]');
    const set  = new Set(seen);
    const state = getState();
    (state.orders || []).forEach(o => { if (set.has(o.id)) o._seen = true; });
  } catch (_) {}
}

// -----------------------------------------------------------------------------
// Inline-Handler-Bindings (func(), kein window.func()) — dieses Modul setzt sie
// selbst, damit die Karten-onclick-Strings unabhängig vom Orchestrator laufen.
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    orderProbesaft,
    orderSetStatus,
    orderCancel,
    orderMarkPaid,
    orderMarkSeen,
    rehydrateOrderSeenFlags
  });
}
